/**
 * Section-level loading skeleton for use within a page.
 * Use when a section (card, list, form block) is loading while rest of page is visible.
 */
import React from 'react';
import SkeletonBlock from './SkeletonBlock';

export interface SectionSkeletonProps {
  /** 'card' | 'list' | 'form' - layout variant */
  variant?: 'card' | 'list' | 'form';
  /** Number of placeholder rows/items */
  rows?: number;
  className?: string;
}

const SectionSkeleton: React.FC<SectionSkeletonProps> = ({
  variant = 'card',
  rows = 3,
  className = '',
}) => {
  if (variant === 'list') {
    return (
      <div className={`space-y-3 ${className}`} aria-hidden="true">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl border border-primary-200/50 p-4">
            <SkeletonBlock className="h-10 w-10 shrink-0 rounded-lg" />
            <div className="min-w-0 flex-1">
              <SkeletonBlock className="mb-2 h-4 w-3/4" />
              <SkeletonBlock className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'form') {
    return (
      <div className={`space-y-4 ${className}`} aria-hidden="true">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i}>
            <SkeletonBlock className="mb-2 h-4 w-24" />
            <SkeletonBlock className="h-10 w-full rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border border-primary-200/50 bg-white p-6 ${className}`} aria-hidden="true">
      <SkeletonBlock className="mb-4 h-5 w-40" />
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonBlock
            key={i}
            className={`h-4 ${i === 0 ? 'w-full' : i === 1 ? 'w-5/6' : 'w-3/4'}`}
          />
        ))}
      </div>
    </div>
  );
};

export default SectionSkeleton;
